import { Button, Card, Table, TableCell, TableHeading, TextArea, Toggle } from "@nike/eds";
import { useState, useEffect } from "react";
import { ShipmentService } from "../shared/ShipmentService";
import useSWR from "swr";
import { useNavigate } from "react-router-dom";
import {CaretRight} from "@nike/nike-design-system-icons";

const shipmentService: ShipmentService = new ShipmentService();

const useShipmentData = (shipment) => {
    const { data, isLoading } = useSWR({
        type: "shipmentInfo",
        shipment: shipment
    }, (data) => shipmentService.getShipmentInfo(data.shipment));

    const dataResult = data?.data?.findShipmentsById;

    const shipmentData = dataResult?.found && dataResult.found.length > 0 ? dataResult.found[0] : undefined;

    return { shipmentData, isLoading };
}

const deconSearchOptions = {
    refreshInterval: 0,
    revalidateOnMount: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: true,
    revalidateIfStale: false,
    loadingTimeout: 30000,
    onError: (error, key) => {
        console.log("Decon search error: ", error, key);
    }
}

const useDeconInSearch = (shipmentInfo, structuredLogsService, permissions) => {
    const { data, isLoading } = useSWR({
        type: "deconInSearch",
        shipment: shipmentInfo
    }, (data) => structuredLogsService.listFlows(["Goods Receipt"], ["PUBLISHED"], undefined, undefined, 
            shipmentInfo?.bolNumber + "-" + shipmentInfo?.containerNumber, 
            undefined, ["TransportHolder"], undefined, undefined, undefined, permissions),
            deconSearchOptions);

    const dataResult = data?.data?.listFlowsDynamicSearch;

    const deconInSearchData = dataResult?.items && dataResult.items.length > 0 ? dataResult.items[0] : undefined;

    return { deconInSearchData, isLoading };
}

const useDeconOutSearch = (shipmentInfo, structuredLogsService, permissions) => {
    const { data, isLoading } = useSWR({
        type: "deconOutSearch",
        shipment: shipmentInfo
    }, (data) => structuredLogsService.listFlows(["Warehouse Removal", "Goods Removal"], ["PUBLISHED"], undefined, undefined, 
            shipmentInfo?.bolNumber + "-" + shipmentInfo?.shipmentNumber + "-" + shipmentInfo?.containerNumber, 
            undefined, undefined, undefined, undefined, undefined, permissions),
            deconSearchOptions);

    const dataResult = data?.data?.listFlowsDynamicSearch;

    const deconOutSearchData = dataResult?.items && dataResult.items.length > 0 ? dataResult.items[0] : undefined;

    return { deconOutSearchData, isLoading };
}

const DeconEvent = ({event}) => {
    if (!event?.timestamp) {
        return null;
    }
    
    const date = new Date(event.timestamp);
    return <>{date.toLocaleString()}</>;
}

const ShipmentCheck = ({shipment, structuredLogsService, filterShipmentsWithErrors, permissions}) => {
    const shipmentSearch = useShipmentData(shipment);
    const deconInSearch = useDeconInSearch(shipmentSearch.shipmentData, structuredLogsService, permissions);
    const deconOutSearch = useDeconOutSearch(shipmentSearch.shipmentData, structuredLogsService, permissions);

    const isDeconOutLoading = shipmentSearch.isLoading || deconOutSearch.isLoading;

    let isInError = false;
    if (!isDeconOutLoading) {
        isInError = !deconOutSearch.deconOutSearchData;
    }

    if (filterShipmentsWithErrors && !isInError) {
        return <></>;
    }

    return <tr>
        <TableCell hasError={isInError}>{shipment}</TableCell>
        <TableCell>{shipmentSearch.isLoading ? "Searching..." : shipmentSearch.shipmentData?.containerNumber}</TableCell>
        <TableCell>{shipmentSearch.isLoading ? "Searching..." : shipmentSearch.shipmentData?.bolNumber}</TableCell>
        <TableCell>{shipmentSearch.isLoading || deconInSearch.isLoading ? "Searching..." : <DeconEvent event={deconInSearch.deconInSearchData} />}</TableCell>
        <TableCell>{isDeconOutLoading ? "Searching..." : <DeconEvent event={deconOutSearch.deconOutSearchData} />}</TableCell>
    </tr>
}

const BULK_SIZE = 19;
const TIMEOUT_IN_SECONDS = 9;

const useThrottledLines = () => {
    const [stringValue, setStringValue] = useState("");
    const [maxLinesToShow, setMaxLinesToShow] = useState(BULK_SIZE);

    const allLines = stringValue.split('\n').filter(line => line.trim() !== '');
    const throttledLines = allLines.slice(0, maxLinesToShow);
    const isThrottling = throttledLines.length < allLines.length;

    useEffect(() => {
        if (allLines.length > maxLinesToShow) {
            const timer = setTimeout(() => {
                setMaxLinesToShow(prev => prev + BULK_SIZE);
            }, TIMEOUT_IN_SECONDS * 1000);

            return () => clearTimeout(timer);
        }
    }, [maxLinesToShow, allLines.length]);

    // Reset maxLinesToShow when string value changes
    useEffect(() => {
        setMaxLinesToShow(BULK_SIZE);
    }, [stringValue]);

    return [stringValue, setStringValue, throttledLines, isThrottling, throttledLines.length * 100 / allLines.length];
};

const marginBottom2Rem = {
        marginBottom: "2rem"
}

const DrsBulkCheckPage = ({structuredLogsService, LocationTracker, permissions}) => {
    const [ shipments, setShipments, shipmentList, isThrottling, percentage ] = useThrottledLines();
    const [ filterShipmentsWithErrors, setFilterShipmentsWithErrors ] = useState(false);

    let navigate = useNavigate();

    const routeChange = () => {
        let path = '/flow-overview';
        navigate(path);
    }

    return <>
           <LocationTracker location="DRS Bulk check" sublocation="Detail"
                            shipments={shipments} isThrottling={isThrottling} filterShipmentsWithErrors={filterShipmentsWithErrors} />
           <Button style={marginBottom2Rem} variant={"ghost"} onClick={() => routeChange()}>
                Events
                <CaretRight size={"s"}/>
           </Button>
           <div className="eds-grid eds-grid--m-cols-4">
            <Card className="eds-grid--m-col-1">
                <TextArea id="shipments" label="Shipments"
                        value={(shipments as string)}
                        onChange={(e) => (setShipments as any)(e.target.value)}
                        minRows={10} maxRows={50}
                        autoResize
                     />
            </Card>
            <Card className="eds-grid--m-col-3">
                <div style={{ marginBottom: '1rem' }}>
                    <Toggle
                        id="filterShipmentsWithErrors"
                        label="Shipments With Errors Only"
                        checked={filterShipmentsWithErrors}
                        onChange={(e) => setFilterShipmentsWithErrors(e.target.checked)}
                    />
                    <div>
                        {isThrottling ? "Running ... " + Math.round(percentage as number) + "%" : undefined}
                    </div>
                </div>
                <Table>
                    <thead>
                        <tr>
                            <TableHeading>Shipment</TableHeading>
                            <TableHeading>Container</TableHeading>
                            <TableHeading>BOL</TableHeading>
                            <TableHeading>Decon in</TableHeading>
                            <TableHeading>Decon out</TableHeading>
                        </tr>
                    </thead>
                    {
                        (shipmentList as any).map((shipment, index) => <ShipmentCheck key={index} 
                                    shipment={shipment} 
                                    structuredLogsService={structuredLogsService} 
                                    filterShipmentsWithErrors={filterShipmentsWithErrors}
                                    permissions={permissions} />)
                    }
                </Table>
            </Card>
        </div>
    </>
}

export default DrsBulkCheckPage;
